import { useQuery } from "@tanstack/react-query"
import { Button, Card, DatePicker, Form, Input, Skeleton, Table, App, Tag } from "antd"
import { ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { ContainerFull } from "../../components/GeneralComponents"
import { getOrders, createInvoice } from "../../endpoints"
import { getUserDetails } from "../../endpoints/userEndpoints"
import { OrderInfoInterface, UserDetailsInterface } from "../../types"

const { TextArea } = Input

interface InvoiceFormData {
    invoiceDate: dayjs.Dayjs
    dueDate: dayjs.Dayjs
    billingContact: string
    billingEmail: string
    billingAddress: string
    institution: string
    discount: number
    notes?: string
}

export const MakeInvoice = (): JSX.Element => {
    const { message } = App.useApp()
    const location = useLocation()
    const navigate = useNavigate()
    const [form] = Form.useForm<InvoiceFormData>()
    const [selectedOrders, setSelectedOrders] = useState<OrderInfoInterface[]>([])
    const [_, setUserDetails] = useState<UserDetailsInterface | null>(null)
    const [loading, setLoading] = useState(false)
    const [loadingUserDetails, setLoadingUserDetails] = useState(false)
    const [currentDiscount, setCurrentDiscount] = useState(0)

    // Get order IDs from navigation state
    const orderIds = location.state?.orderIds as number[] || []

    const { isPending, isError, data, error } = useQuery({
        queryKey: ["getorders", "all"],
        queryFn: () => getOrders({ type: "all" }),
        enabled: orderIds.length > 0,
    })

    // Helper function to populate user billing information
    const populateUserBillingInfo = (user: UserDetailsInterface) => {
        const billingAddress = user.billingAddress
        form.setFieldsValue({
            billingContact: billingAddress?.billingContactName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            billingEmail: billingAddress?.billingEmail || user.email || '',
            institution: user.institution || '',
            billingAddress: [
                billingAddress?.line1,
                billingAddress?.line2,
                `${billingAddress?.city || ''} ${billingAddress?.state || ''} ${billingAddress?.zip || ''}`.trim(),
                billingAddress?.country
            ].filter(Boolean).join('\n') || ''
        })
        console.log("Using user billing information for invoice form")
    }

    useEffect(() => {
        if (data?.code === 1000 && data.orders) {
            // Filter orders based on selected IDs
            const filteredOrders = data.orders.filter(order => orderIds.includes(order.id!))
            setSelectedOrders(filteredOrders)
            
            // Fetch user details for billing info if we have orders
            if (filteredOrders.length > 0) {
                const firstUserId = filteredOrders[0].user.id
                if (firstUserId) {
                    setLoadingUserDetails(true)
                    
                    // Fetch user details first
                    getUserDetails({ userId: firstUserId })
                        .then(userResponse => {
                            console.log("Fetched user details:", userResponse)
                            if (userResponse.code === 1000 && userResponse.user) {
                                setUserDetails(userResponse.user)
                                
                                // Check if user has a group with billing information
                                // Prioritize group billing if the user belongs to a group
                                if (userResponse.user.group) {
                                    const group = userResponse.user.group
                                    
                                    // Check if group has any billing information (more flexible check)
                                    const hasGroupBilling = group.billingContactName || 
                                                          group.billingEmail || 
                                                          group.billingInstitution || 
                                                          group.billingAddressLine1 ||
                                                          group.billingCity
                                    
                                    if (hasGroupBilling) {
                                        // Use group billing information (fallback to user info where group info is missing)
                                        form.setFieldsValue({
                                            billingContact: group.billingContactName || `${userResponse.user.firstName || ''} ${userResponse.user.lastName || ''}`.trim(),
                                            billingEmail: group.billingEmail || userResponse.user.email || '',
                                            institution: group.billingInstitution || userResponse.user.institution || '',
                                            billingAddress: [
                                                group.billingAddressLine1,
                                                group.billingAddressLine2,
                                                `${group.billingCity || ''} ${group.billingState || ''} ${group.billingZipcode || ''}`.trim(),
                                                group.billingCountry
                                            ].filter(Boolean).join('\n') || ''
                                        })
                                        console.log("Using group billing information for invoice form")
                                    } else {
                                        // Group exists but has no billing info, use user billing information
                                        populateUserBillingInfo(userResponse.user)
                                        console.log("Group exists but no billing info, using user billing information")
                                    }
                                } else {
                                    // No group, use user billing information
                                    populateUserBillingInfo(userResponse.user)
                                    console.log("No group found, using user billing information")
                                }
                            }
                        })
                        .catch(error => {
                            console.error('Error fetching user details:', error)
                            message.error('Failed to load billing information')
                        })
                        .finally(() => {
                            setLoadingUserDetails(false)
                        })
                }
            }
        }
    }, [data, orderIds, form, message])

    useEffect(() => {
        // Set default dates
        const today = dayjs()
        const dueDate = today.add(30, 'day') // 30 days from today
        
        form.setFieldsValue({
            invoiceDate: today,
            dueDate: dueDate,
        })
    }, [form])


    if (orderIds.length === 0) {
        return (
            <ContainerFull>
                <Card title="Make Invoice" style={{ maxWidth: 600, margin: '0 auto', marginTop: 50 }}>
                    <p>No orders selected for invoice generation.</p>
                    <Button onClick={() => navigate('/admin/orders')}>
                        Back to Orders
                    </Button>
                </Card>
            </ContainerFull>
        )
    }

    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return (
            <ContainerFull>
                <Card title="Error" style={{ maxWidth: 600, margin: '0 auto', marginTop: 50 }}>
                    <p>Error loading orders: {error?.message}</p>
                    <Button onClick={() => navigate('/admin/orders')}>
                        Back to Orders
                    </Button>
                </Card>
            </ContainerFull>
        )
    }

    const getOrderAmount = (order: OrderInfoInterface) => {
        if (order.billing_type === 'free rerun') {
            return 0
        }

        return Number(order.totalPrice?.price || 0)
    }

    const columns: ColumnsType<OrderInfoInterface> = [
        {
            title: "Order ID",
            dataIndex: "id",
            key: "id",
            width: 100,
        },
        {
            title: "Order Name",
            dataIndex: "ordername",
            key: "ordername",
            width: 200,
        },
        {
            title: "Customer",
            dataIndex: "user",
            key: "customer",
            width: 200,
            render: (user) => `${user.firstName} ${user.lastName}`,
        },
        {
            title: "Service",
            dataIndex: "productname",
            key: "service",
            width: 150,
        },
        {
            title: "Billing Type",
            dataIndex: "billing_type",
            key: "billing_type",
            width: 130,
            render: (billingType) => {
                const normalizedBillingType = billingType || 'normal'
                const color = normalizedBillingType === 'free rerun'
                    ? 'orange'
                    : normalizedBillingType === 'internal'
                        ? 'green'
                        : 'default'

                return <Tag color={color}>{normalizedBillingType}</Tag>
            },
        },
        {
            title: "Samples",
            dataIndex: "samples",
            key: "samples",
            width: 80,
            align: "center",
            render: (samples) => samples ? samples.length : 0,
        },
        {
            title: "Amount",
            dataIndex: "totalPrice",
            key: "amount",
            width: 120,
            render: (_, record) => `$${getOrderAmount(record).toFixed(2)}`,
        },
        {
            title: "Date",
            dataIndex: "ts",
            key: "ts",
            width: 120,
            render: (ts) => ts ? dayjs(ts).format('MM/DD/YYYY') : 'N/A',
        },
    ]

    const calculateSubtotal = () => {
        return selectedOrders.reduce((sum, order) => sum + getOrderAmount(order), 0)
    }

    const calculateTotalSamples = () => {
        return selectedOrders.reduce((sum, order) => sum + (order.samples?.length || 0), 0)
    }

    const sampleBreakdownByService = Object.entries(
        selectedOrders.reduce<Record<string, number>>((breakdown, order) => {
            if (order.billing_type === 'free rerun') {
                return breakdown
            }

            const serviceName = order.productname || 'Unknown Service'
            breakdown[serviceName] = (breakdown[serviceName] || 0) + (order.samples?.length || 0)
            return breakdown
        }, {})
    )

    const calculateTotal = (discount: number = 0) => {
        const subtotal = calculateSubtotal()
        return Math.max(0, subtotal - discount) // Ensure total doesn't go below 0
    }

    const handleSubmit = async (values: InvoiceFormData) => {
        setLoading(true)
        try {
            console.log('Submitting invoice with values:', values)
            const response = await createInvoice({
                orderIds: selectedOrders.map(order => order.id!),
                invoiceDate: values.invoiceDate.format('YYYY-MM-DD'),
                dueDate: values.dueDate.format('YYYY-MM-DD'),
                billingContact: values.billingContact,
                billingEmail: values.billingEmail,
                billingAddress: values.billingAddress,
                institution: values.institution,
                discount: values.discount || 0,
                notes: values.notes || '',
            })
            
            if (response.code === 1000) {
                message.success(`Invoice created successfully! Invoice ID: ${response.invoiceId}`)
                navigate('/admin/orders')
            } else {
                message.error(response.message || 'Failed to create invoice')
            }
        } catch (error) {
            console.error('Error creating invoice:', error)
            message.error('Failed to create invoice')
        } finally {
            setLoading(false)
        }
    }
    // console.log('selectedOrders', selectedOrders)
    // console.log('userDetails', userDetails)
    return (
        <ContainerFull>
            <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
                <Card title="Create Invoice" style={{ marginBottom: 20 }}>
                    <div style={{ marginBottom: 20 }}>
                        <h3>Selected Orders ({selectedOrders.length})</h3>
                        <div style={{ marginBottom: 8 }}>Total samples selected: <strong>{calculateTotalSamples()}</strong></div>
                        <Table
                            columns={columns}
                            dataSource={selectedOrders}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            summary={() => (
                                <Table.Summary fixed>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={5}>
                                            <strong>Subtotal:</strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={5}>
                                            <strong>${calculateSubtotal().toFixed(2)}</strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={6} />
                                    </Table.Summary.Row>
                                    {currentDiscount > 0 && (
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={5}>
                                                <strong>Discount:</strong>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={5}>
                                                <strong style={{ color: '#52c41a' }}>-${currentDiscount.toFixed(2)}</strong>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={6} />
                                        </Table.Summary.Row>
                                    )}
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={5}>
                                            <strong>Total Amount:</strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={5}>
                                            <strong>${calculateTotal(currentDiscount).toFixed(2)}</strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={6} />
                                    </Table.Summary.Row>
                                </Table.Summary>
                            )}
                        />
                        <div style={{ marginTop: 12 }}>
                            <strong>Samples by service type excluding free reruns:</strong>
                            <div style={{ marginTop: 4, color: '#595959' }}>
                                {sampleBreakdownByService.length > 0
                                    ? sampleBreakdownByService.map(([serviceName, sampleCount]) => `${serviceName}: ${sampleCount}`).join(' | ')
                                    : 'No billable samples selected'}
                            </div>
                        </div>
                    </div>

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        style={{ marginTop: 30 }}
                    >
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div>
                                <h4>Invoice Details</h4>
                                <Form.Item
                                    name="invoiceDate"
                                    label="Invoice Date"
                                    rules={[{ required: true, message: 'Please select invoice date' }]}
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>

                                <Form.Item
                                    name="dueDate"
                                    label="Due Date"
                                    rules={[{ required: true, message: 'Please select due date' }]}
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </div>

                            <div>
                                <h4>Billing Information {loadingUserDetails && <span style={{ fontSize: '12px', color: '#666' }}>(Loading billing information...)</span>}</h4>
                                <Form.Item
                                    name="institution"
                                    label="Institution/Organization"
                                    rules={[{ required: true, message: 'Please enter institution or organization' }]}
                                >
                                    <Input 
                                        placeholder="Enter institution or organization name" 
                                        disabled={loadingUserDetails}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="billingContact"
                                    label="Billing Contact"
                                    rules={[{ required: true, message: 'Please enter billing contact' }]}
                                >
                                    <Input 
                                        placeholder="Enter billing contact name" 
                                        disabled={loadingUserDetails}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="billingEmail"
                                    label="Billing Email"
                                    rules={[
                                        { required: true, message: 'Please enter billing email' },
                                        { type: 'email', message: 'Please enter a valid email' }
                                    ]}
                                >
                                    <Input 
                                        placeholder="Enter billing email" 
                                        disabled={loadingUserDetails}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="billingAddress"
                                    label="Billing Address"
                                    rules={[{ required: true, message: 'Please enter billing address' }]}
                                >
                                    <TextArea 
                                        rows={4} 
                                        placeholder="Enter billing address"
                                        disabled={loadingUserDetails}
                                    />
                                </Form.Item>
                            </div>
                        </div>

                        {/* Discount Section */}
                        <div style={{ marginTop: 30, display: 'flex', justifyContent: 'flex-end' }}>
                            <div style={{ width: 300 }}>
                                <Form.Item
                                    name="discount"
                                    label="Apply Discount ($)"
                                    rules={[
                                        { 
                                            validator: (_, value) => {
                                                if (value === undefined || value === '' || value === null) {
                                                    return Promise.resolve() // Allow empty values
                                                }
                                                const numValue = parseFloat(value)
                                                if (isNaN(numValue) || numValue < 0) {
                                                    return Promise.reject(new Error('Discount must be 0 or greater'))
                                                }
                                                return Promise.resolve()
                                            }
                                        }
                                    ]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max={calculateSubtotal()}
                                        placeholder="Enter discount amount"
                                        style={{ width: '100%' }}
                                        onChange={(e) => {
                                            const value = parseFloat(e.target.value) || 0
                                            setCurrentDiscount(value)
                                        }}
                                    />
                                </Form.Item>
                            </div>
                        </div>

                        <div style={{ marginTop: 30 }}>
                            <Form.Item
                                name="notes"
                                label="Invoice Notes"
                                style={{ marginBottom: 0 }}
                            >
                                <TextArea
                                    rows={6}
                                    placeholder="Add notes for this invoice"
                                />
                            </Form.Item>
                        </div>

                        <div style={{ marginTop: 30, textAlign: 'center' }}>
                            <Button 
                                onClick={() => navigate('/admin/orders')} 
                                style={{ marginRight: 10 }}
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                loading={loading}
                                disabled={loadingUserDetails}
                            >
                                Create Invoice
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>
        </ContainerFull>
    )
}
